import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Dialog'
import { Portal } from 'reakit/Portal'

import { isIpad, isMobile } from '../../../app/shared/isMobile'
import { useScreenSize } from '../../hooks/use-screen-size'
import { useVisualViewport } from '../../hooks/useVisualViewport'
import { ChildPosition, getFlexAlignClasses } from '../../util'
import { DialogBox, DialogBoxProps } from './dialog-box'
import { DialogOverlay } from './dialog-overlay'
import { DialogDragContext, DialogStateReturn, ZIndexContext, useDrag } from './hooks'
import { getExtendedDialogState } from './utils'

export enum DialogType {
  modal,
  drawer,
}

export interface DialogProps extends DialogBoxProps, Pick<Reakit.DialogOptions, 'hideOnEsc' | 'hide'> {
  /**
   * The returned object from `useDialogState`
   */
  state: DialogStateReturn

  /**
   * Any additional props to the Reakit Dialog component
   */
  finalFocusRef?: React.RefObject<HTMLElement>

  /**
   * Callback which is called when the dialog closes and all animations complete
   */
  onHide?: () => void

  /**
   * Callback which is called when the dialog overlay is clicked
   * default behavior (closing the dialog) will be overridden so it has to be called manually
   */
  onOverlayClick?: () => void

  /**
   * Sets if the dialog can be dragged across the screen
   *
   * @default false
   */
  isDraggable?: boolean

  /**
   * Type of dialog
   *
   * @default modal
   */
  type?: keyof typeof DialogType

  /**
   * Dialog position on the screen
   *
   * @default center
   */
  position?: keyof typeof ChildPosition

  overlayClass?: string

  /**
   * Sets if backdrop overlay prevents interaction with rest of page, onOverlayClick will have no effect if isBackdropDisabled is true
   *
   * @default false
   */
  isBackdropDisabled?: boolean
}

const DIALOG_BACKDROP_WIDTH_CSS_VAR = '--dialog-backgrop-width'

const onResize = () => {
  // Save to css var because it can change and that could make modal dialog jump on open
  document.body.style.setProperty(
    DIALOG_BACKDROP_WIDTH_CSS_VAR,
    `${Math.max(document.documentElement.clientWidth, window.innerWidth)}px`
  )
}

/**
 * The actual dialog component which renders overlay, box and handles all the
 * functionality like focus trapping, etc.
 */
function DialogComponent({
  state,
  finalFocusRef,
  onHide,
  onOverlayClick,
  children,
  hide,
  hideOnEsc,
  isDraggable = false,
  position = 'center',
  isBackdropDisabled = false,
  type = 'modal',
  overlayClass,
  'aria-label': ariaLabel,
  ...props
}: React.PropsWithChildren<DialogProps>) {
  const dialogDrag = useDrag(isDraggable)
  const { isFullyClosed, isFullyOpened } = getExtendedDialogState(state)
  const { viewport } = useVisualViewport({ enabled: !isFullyClosed, onScroll: false })
  const screenSize = useScreenSize()

  const dialogStyle = React.useMemo(() => {
    if (isMobile() && !isIpad() && viewport.scale !== 1) {
      return {
        left: viewport.left,
        top: viewport.top,
        width: viewport.unscaledWidth,
        height: viewport.unscaledHeight,
        minWidth: screenSize.width,
        minHeight: screenSize.width,
        willChange: 'auto',
      }
    }

    return { width: `var(${DIALOG_BACKDROP_WIDTH_CSS_VAR})` }
  }, [screenSize.width, viewport.left, viewport.scale, viewport.top, viewport.unscaledHeight, viewport.unscaledWidth])

  React.useEffect(() => {
    if (isFullyClosed) {
      return
    }

    window.addEventListener('resize', onResize)
    onResize()

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [isFullyClosed])

  if (isFullyClosed) {
    return null
  }

  const DialogWrapper = state.modal ? React.Fragment : Portal
  return (
    <DialogWrapper>
      <Reakit.Dialog
        {...state}
        hideOnClickOutside={false}
        hideOnEsc={hideOnEsc}
        hide={hide ?? state.hide}
        aria-label={ariaLabel}
        unstable_finalFocusRef={finalFocusRef}
      >
        {({ onTransitionEnd, onAnimationEnd, ...innerProps }) => (
          <div
            {...innerProps}
            className={classnames('fixed left-0 top-0 z-dialog outline-none', {
              'finviz-modal': DialogType[type] === DialogType.modal,
              'finviz-drawer': DialogType[type] === DialogType.drawer,
              'pointer-events-none': isBackdropDisabled,
            })}
            data-fully-open={isFullyOpened}
          >
            <div
              onTransitionEnd={onTransitionEnd}
              onAnimationEnd={onAnimationEnd}
              className={classnames(
                'fixed bottom-0 left-0 right-0 top-0 flex max-w-full transform ipad-fullscreen:pt-5',
                DialogType[type] === DialogType.modal && getFlexAlignClasses(ChildPosition[position]),
                {
                  'z-dialog p-2 sm:p-5': DialogType[type] === DialogType.modal,
                  'justify-end': DialogType[type] === DialogType.drawer,
                }
              )}
              style={dialogStyle}
            >
              {!isBackdropDisabled && (
                <DialogOverlay className={overlayClass} onClick={onOverlayClick ?? hide ?? state.hide} />
              )}
              <DialogDragContext.Provider value={dialogDrag}>
                <ZIndexContext.Provider value="z-dialog">
                  <DialogBox
                    {...props}
                    className={classnames(props.className, {
                      'pointer-events-auto': isBackdropDisabled,
                    })}
                    onUnmount={() => {
                      onHide?.()
                      dialogDrag.resetBoxPosition()
                    }}
                  >
                    {children}
                  </DialogBox>
                </ZIndexContext.Provider>
              </DialogDragContext.Provider>
            </div>
          </div>
        )}
      </Reakit.Dialog>
    </DialogWrapper>
  )
}

export function Dialog(props: React.PropsWithChildren<DialogProps>) {
  const { isFullyClosed } = getExtendedDialogState(props.state)

  return isFullyClosed ? null : <DialogComponent {...props} />
}
